import { AxiosRequestConfig } from "axios";
import { toast } from "react-toastify";
import { ApiService } from "../../../config";
import { handleError } from "../../../utils/parseApiReturn";
import { handleToken } from "../../../utils/expiredTokenLogout";

const googleCallback = async (code: string, token: string) => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const result = await ApiService.post(
      "/imports-manager/oauth/google/callback",
      { code },
      config
    );

    // Retorna um objeto indicando sucesso
    // Backend retorna { data: null, message: "sucesso", error: false }
    return {
      success: !result.data.error,
      message: result.data.message,
      data: result.data.data,
    };
  } catch (error: unknown) {
    const { message } = handleError(
      error,
      "Erro inesperado ao conectar o e-mail."
    );
    toast.error(message);
    return {
      success: false,
      message: message,
      data: null,
    };
  }
};

/** @deprecated Usar oauthGoogle() em vez disso */
const initiateGoogleOAuthForEmail = async (
  bearerToken: string
): Promise<void> => {
  try {
    // Obtém a URL base da sua API a partir da configuração do ApiService
    const baseUrl = import.meta.env.VITE_APP_URL_BASE;
    if (!baseUrl) {
      toast.error("A URL da API não está configurada.");
      return;
    }
    console.log(bearerToken);
    // SOLUÇÃO: Construir a URL do Google OAuth diretamente no frontend
    // baseando-se na mesma lógica que o backend usa
    const clientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID;
    const redirectUri = `${window.location.origin}/auth/manager/google/callback`;
    const scopes = [
      "openid",
      "https://www.googleapis.com/auth/userinfo.profile",
      "https://www.googleapis.com/auth/userinfo.email",
      "https://www.googleapis.com/auth/business.manage",
    ].join(" ");

    const authUrl = new URL("https://accounts.google.com/o/oauth2/auth");
    authUrl.searchParams.set("client_id", clientId);
    authUrl.searchParams.set("redirect_uri", redirectUri);
    authUrl.searchParams.set("response_type", "code");
    authUrl.searchParams.set("scope", scopes);
    authUrl.searchParams.set("access_type", "offline");
    authUrl.searchParams.set("approval_prompt", "force");

    // Redireciona o navegador para a URL do Google OAuth
    window.location.href = authUrl.toString();
  } catch (error: unknown) {
    const { message } = handleError(
      error,
      "Erro inesperado ao iniciar a conexão com o Google."
    );
    toast.error(message);
  }
};

const importsManager = async (token: string, sort?: "asc" | "desc") => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      params: sort ? { sort } : undefined,
    };
    const result = await ApiService.get("/imports-manager", config);
    const { data, error, message: responseMessage } = result.data;
    if (error) {
      const message = responseMessage || "Erro ao obter perfis em acompanhamento.";
      handleToken(message);
      toast.error(message);
      return null;
    }
    return data;
  } catch (error) {
    const { message } = handleError(
      error,
      "Erro inesperado ao obter perfis em acompanhamento."
    );
    toast.error(message);
    return null;
  }
};

const oauthGoogle = async (token: string) => {
  try {
    // Salva o token no sessionStorage para recuperar após o callback
    sessionStorage.setItem("oauth_token", token);

    // Obtém a URL base da API
    const baseUrl = import.meta.env.VITE_APP_URL_BASE;
    if (!baseUrl) {
      toast.error("A URL da API não está configurada.");
      return null;
    }

    // Redireciona para o endpoint do backend que gerencia OAuth do Google para managers
    // O backend usa @GetMapping("/oauth/google") e NÃO requer autenticação (@PreAuthorize comentado)
    // Por isso, fazemos um redirect direto sem passar o token na URL
    window.location.href = `${baseUrl}/imports-manager/oauth/google`;
  } catch (error: unknown) {
    const { message } = handleError(
      error,
      "Erro inesperado ao iniciar conexão com Google."
    );
    toast.error(message);
  }
};

const createBatchImport = async (managerId: number, token: string) => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const result = await ApiService.post(
      `/imports-manager/${managerId}/create-batch-import`,
      undefined,
      config
    );
    return result.data.data;
  } catch (error) {
    const { message } = handleError(
      error,
      "Erro inesperado ao obter perfis em acompanhamento."
    );
    toast.error(message);
    return null;
  }
};

const resyncManager = async (managerId: string) => {
  try {
    const result = await ApiService.post(`/imports-manager/${managerId}/resync`);
    const message = result.data.message || "Re-sincronização iniciada com sucesso!";

    if (result.data.error) {
      toast.error(message);
      return {
        success: false,
        message,
      };
    }

    toast.success(message);
    return {
      success: true,
      message,
    };
  } catch (error) {
    const { message } = handleError(error, "Erro ao iniciar re-sincronização.");
    toast.error(message);
    return {
      success: false,
      message: message,
    };
  }
};

const getManagerProfiles = async (batchId: string, token: string) => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const result = await ApiService.get(
      `/imports-manager/${batchId}/profiles`,
      config
    );
    const { data, error, message: responseMessage } = result.data;
    if (error) {
      toast.error(responseMessage || "Erro ao listar perfis do lote.");
      return null;
    }
    return data;
  } catch (error) {
    const { message } = handleError(error, "Erro ao listar perfis do lote.");
    toast.error(message);
    return null;
  }
};

const syncProfilesManually = async (token: string) => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const result = await ApiService.post(
      "/imports-manager/sync-profiles",
      undefined,
      config
    );
    const message = result.data.message || "Sincronização iniciada com sucesso!";

    if (result.data.error) {
      toast.error(message);
      return {
        success: false,
        message,
      };
    }

    toast.success(message);
    return {
      success: true,
      message,
    };
  } catch (error) {
    const { message } = handleError(
      error,
      "Erro ao iniciar sincronização de perfis."
    );
    toast.error(message);
    return {
      success: false,
      message: message,
    };
  }
};

const processImportsManually = async (token: string) => {
  try {
    const config: AxiosRequestConfig = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
    const result = await ApiService.post(
      "/imports-manager/process-imports",
      undefined,
      config
    );
    const message = result.data.message || "Processamento iniciado com sucesso!";

    if (result.data.error) {
      toast.error(message);
      return {
        success: false,
        message,
      };
    }

    toast.success(message);
    return {
      success: true,
      message,
    };
  } catch (error) {
    const { message } = handleError(
      error,
      "Erro ao iniciar processamento de importações."
    );
    toast.error(message);
    return {
      success: false,
      message: message,
    };
  }
};

export const EmailService = {
  googleCallback,
  getManagerProfiles,
  initiateGoogleOAuthForEmail,
  importsManager,
  createBatchImport,
  oauthGoogle,
  resyncManager,
  syncProfilesManually,
  processImportsManually,
};
